import { SolidSnackbar } from '@/components/snackbar';
import {
  SnackbarProvider as CustomSnackbarProvider,
  SnackbarContent,
  CustomContentProps,
} from 'notistack';
import { forwardRef, useEffect, type PropsWithChildren } from 'react';
import { KeenIcon } from '@/components';
import { announceStatus, announceAlert } from '@/components/StatusAnnouncer';

// ---------------------------------------------------------------------------
// Per-variant config
// ---------------------------------------------------------------------------
type StandardVariant = 'default' | 'success' | 'info' | 'warning' | 'error';

const variantStyles: Record<StandardVariant, string> = {
  default: 'bg-gray-100 text-gray-700 dark:bg-coal-300 dark:text-gray-900',
  success: 'bg-success text-[#052e16] dark:bg-[#047857] dark:text-white contrast:text-success-inverse',
  info: 'bg-info text-info-inverse',
  warning: 'bg-warning text-warning-inverse dark:bg-[#854d0e] dark:text-white',
  error: 'bg-danger text-danger-inverse dark:bg-[#D81A48] dark:text-white',
};

const variantIcons: Record<StandardVariant, string> = {
  default: 'information-2',
  success: 'check-circle',
  info: 'information-2',
  warning: 'warning-2',
  error: 'cross-circle',
};

// ---------------------------------------------------------------------------
// AccessibleSnack — visual snackbar + StatusAnnouncer hook on mount
// ---------------------------------------------------------------------------
interface AccessibleSnackProps extends Omit<CustomContentProps, 'variant'> {
  variant?: StandardVariant;
}

const AccessibleSnack = forwardRef<HTMLDivElement, AccessibleSnackProps>(
  ({ message, variant = 'default' }, ref) => {
    const styles = variantStyles[variant] ?? variantStyles.default;
    const icon = variantIcons[variant] ?? 'information-2';

    // WCAG 4.1.3 — announce via the top-level StatusAnnouncer live-region.
    // We do this on mount so the announcement fires exactly once when the
    // snackbar first appears. Using the singleton bypasses the nested
    // live-region problem inherent in notistack's portal container.
    useEffect(() => {
      const text = typeof message === 'string' ? message : '';
      if (!text) return;
      if (variant === 'error' || variant === 'warning') {
        announceAlert(text);
      } else {
        announceStatus(text);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only — message content is stable for the lifetime of this snack

    return (
      // No role/aria-live here — announcement is handled by StatusAnnouncer.
      // Adding role/aria-live inside notistack's portal creates nested live
      // regions which screen readers ignore.
      <SnackbarContent ref={ref}>
        <div
          className={`flex grow items-center gap-2.5 rounded-md py-2.5 px-3 shadow-default ${styles}`}
          aria-hidden="true"
        >
          <KeenIcon icon={icon} className="text-lg" aria-hidden="true" />
          <span className="text-sm">{message}</span>
        </div>
      </SnackbarContent>
    );
  }
);
AccessibleSnack.displayName = 'AccessibleSnack';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
const SnackbarProvider = ({ children }: PropsWithChildren) => {
  return (
    <CustomSnackbarProvider
      autoHideDuration={2000}
      maxSnack={3}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      Components={{
        solid: SolidSnackbar,
        default: AccessibleSnack,
        success: AccessibleSnack,
        info: AccessibleSnack,
        warning: AccessibleSnack,
        error: AccessibleSnack,
      }}
      // No role/aria-live on the outer container — AT announcements are
      // handled exclusively by the StatusAnnouncer singleton live-regions.
    >
      {children}
    </CustomSnackbarProvider>
  );
};

export { SnackbarProvider };
